import type { DateTime } from "luxon"

import type { AppLocalization } from "../../../../../../localization/appLocalization"
import type { DateTimeFactory } from "../../../../../../localization/useDateTime"
import type { MeetingRowOrder } from "../../../weekMenuTypes"

export type MeetingDayGroup<T extends MeetingRowOrder> = Readonly<{
    key: string
    label: string
    meetings: T[]
}>

type DayLabelParams = Readonly<{
    start: DateTime | null
    startIso: string | null
    todayIso: string | null
    tomorrowIso: string | null
    dateTimeFactory: DateTimeFactory
    l10n: AppLocalization
}>

function getDayLabel({ start, startIso, todayIso, tomorrowIso, dateTimeFactory, l10n }: DayLabelParams): string {
    if (!start || !startIso) return l10n.getString("user-home-meeting-day-unknown")
    if (startIso === todayIso) return l10n.getString("user-home-meeting-day-today")
    if (startIso === tomorrowIso) return l10n.getString("user-home-meeting-day-tomorrow")

    return dateTimeFactory.formatLongDate(start)
}

export function groupMeetingsByDay<T extends MeetingRowOrder>(
    meetings: readonly T[],
    dateTimeFactory: DateTimeFactory,
    l10n: AppLocalization,
): MeetingDayGroup<T>[] {
    const todayIso = dateTimeFactory.now().toISODate()
    const tomorrowIso = dateTimeFactory.now().plus({ days: 1 }).toISODate()
    const byKey = new Map<string, { label: string; meetings: T[] }>()

    for (const meeting of meetings) {
        const startStr = meeting.meetingDetails.startDateTime || meeting.deliveryDetails.time
        const parsed = startStr ? dateTimeFactory.fromApi(startStr).startOf("day") : null
        const start = parsed?.isValid ? parsed : null
        const startIso = start?.toISODate() ?? null
        const key = startIso ?? `unknown-${meeting.id}`

        const existing = byKey.get(key)
        if (existing) {
            existing.meetings.push(meeting)
            continue
        }

        const label = getDayLabel({ start, startIso, todayIso, tomorrowIso, dateTimeFactory, l10n })
        byKey.set(key, { label, meetings: [meeting] })
    }

    return Array.from(byKey, ([key, group]) => ({ key, ...group }))
}
